import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getApiErrorMessage } from "../lib/apiErrors";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  FileText, ArrowLeft, MapPin, Calendar, User, Plane, Target, 
  CheckCircle2, XCircle, AlertTriangle, Clock, FileCheck, 
  ChevronDown, ChevronUp, Save, RefreshCw, Download, Globe,
  Upload, Paperclip, Image, Trash2, Eye, Map, Pencil, PlayCircle,
  Shield, AlertOctagon, Radio, Copy, ExternalLink, Info, Radar, Sparkles,
  Send, FolderUp, FileUp, Users, Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import MissionContactsCard from "../components/mission/MissionContactsCard";
import PopulationDensityPreview from "../components/mission/PopulationDensityPreview";
import { OperationsPlanningEditButton } from "../components/mission/OperationsPlanningEditButton";
import { editFromDetailState } from "../lib/missionEditNavigation";
import { localizeReasonMessage, hasSailWarningReason, sailWarningTooltip } from "../lib/provisionalReasons";
import {
  arcBannerReason, arcNotes, groupProvisionalReasons, isWarningGroup, localizeArcLine, provisionalGroupTitle,
} from "../lib/arcDisplay";
import { formatIsoDateDmy } from "../lib/dateFormat";
import { toast } from "sonner";
import { MissionLocationMap } from "../components/MissionLocationMap";
import {
  classifyFlightArea, FLIGHT_AREA_KIND, storedFlightAreaNeedsFix, useFlightAreaSettings, useFlightAreaValidation,
} from "../lib/flightArea";
import { SoraAssistant } from "../components/SoraAssistant";
// Sprint Y Block E - Correspondence Tab
import { CorrespondenceTab } from "../components/correspondence";
import { OperatorDeclarationsCard } from "../components/sora/OperatorDeclarationsCard";
import { AtypicalAirspaceDeclarationCard } from "../components/sora/AtypicalAirspaceDeclarationCard";
import { DeclarationOverrideModal } from "../components/sora/DeclarationOverrideModal";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { FeatureGate } from "../components/FeatureLockedOverlay";


export const MissionDetail = () => {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const { hasFeature } = useFeatureFlag();
  
  const [mission, setMission] = useState(null);
  const [pilot, setPilot] = useState(null);
  const [aircraft, setAircraft] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [scenarios, setScenarios] = useState([]); // All approved scenarios for dropdown
  const [documents, setDocuments] = useState([]);
  const [missionDocuments, setMissionDocuments] = useState([]); // Documents attached to this mission
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [checklist, setChecklist] = useState([]);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [generatingSoraPdf, setGeneratingSoraPdf] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const fileInputRef = useRef(null);
  const documentFileInputRef = useRef(null);
  
  // Tab state for hiding SAIL banner when CheckMate is active
  const [activeTab, setActiveTab] = useState("details");
  
  // Ref for CheckMate tab to enable scrolling
  const checkMateTabRef = useRef(null);
  const operatorDeclarationsRef = useRef(null);
  const populationDensityRef = useRef(null);
  
  // UCL Submission state
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [creatingSubmission, setCreatingSubmission] = useState(false);
  
  // Scenario update state
  const [updatingScenario, setUpdatingScenario] = useState(false);
  
  // Document upload state for mission-specific documents
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  // SORA Pre-Assessment state
  const [soraAssessment, setSoraAssessment] = useState(null);
  const [soraLoading, setSoraLoading] = useState(false);
  const [environmentType, setEnvironmentType] = useState("rural");

  // Saved (persisted) manual EASA population-density assessment — reported up by
  // PopulationDensityPreview so the SAIL banner's environment badge can show it alongside the
  // environment type. Display only — does not feed the SAIL/GRC calculation itself (see decisions.md).
  const [populationAssessment, setPopulationAssessment] = useState(null);
  useEffect(() => { setPopulationAssessment(null); }, [mission?.id]);
  // "Doplnit" target from a provisional-iGRC reason (ProvisionalReason.target — see
  // SoraProvisionalReasonCodes.Targets): which detail card to force-expand + scroll to.
  const [forceExpandCard, setForceExpandCard] = useState(null);
  
  // Airspace Advisory state
  const [airspaceAdvisory, setAirspaceAdvisory] = useState(null);
  const [airspaceLoading, setAirspaceLoading] = useState(false);
  
  // Compliance Detail state
  const [complianceDetail, setComplianceDetail] = useState(null);
  
  // Insurance status state
  const [insuranceStatus, setInsuranceStatus] = useState(null);
  
  // U-Space zone detection state
  const [isInUSpaceZone, setIsInUSpaceZone] = useState(false);
  
  // Wave 2.5: Declaration Override Modal state
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);

  // Translation helper for checklist section names from backend
  const translateChecklistSection = (sectionName) => {
    const sectionMap = {
      'Pre-Flight Planning': t.checklist?.preFlightPlanning,
      'Equipment Check': t.checklist?.equipmentCheck,
      'Pilot Readiness': t.checklist?.pilotReadiness,
      'Operational Area': t.checklist?.operationalArea,
    };
    return sectionMap[sectionName] || sectionName;
  };

  // Translation helper for checklist item labels from backend
  const translateChecklistItem = (itemLabel) => {
    const itemMap = {
      'Mission date set': t.checklist?.missionDateSet,
      'Location defined': t.checklist?.locationDefined,
      'Aircraft assigned': t.checklist?.aircraftAssigned,
      'Pilot assigned': t.checklist?.pilotAssignedItem,
      'Scenario selected': t.checklist?.scenarioSelected,
      'Aircraft operational': t.checklist?.aircraftOperational,
      'Battery charged': t.checklist?.batteryCharged,
      'Equipment inspected': t.checklist?.equipmentInspected,
      'Pilot certification valid': t.checklist?.pilotCertValid,
      'Pilot briefed': t.checklist?.pilotBriefed,
      'Weather checked': t.checklist?.weatherChecked,
      'Airspace checked': t.checklist?.airspaceChecked,
      'NOTAM reviewed': t.checklist?.notamReviewed,
      'Area secured': t.checklist?.areaSecured,
    };
    return itemMap[itemLabel] || itemLabel;
  };

  // Translation helper for SORA risk factors from backend
  const translateRiskFactor = (factor) => {
    // Risk increases
    if (factor.includes('Airspace assessment: caution')) {
      return t.sora?.riskAirspaceAssessmentCaution || factor;
    }
    if (factor.includes('Airspace assessment: high')) {
      return t.sora?.riskAirspaceAssessmentHighRisk || factor;
    }
    if (factor.includes('Location precision is low')) {
      return t.sora?.riskLocationPrecisionLow || factor;
    }
    if (factor.includes('Urban environment')) {
      return t.sora?.riskUrbanEnvironment || factor;
    }
    if (factor.includes('BVLOS operation')) {
      return t.sora?.riskBvlosOperation || factor;
    }
    if (factor.includes('Proximity to')) {
      // Extract airport and distance
      const match = factor.match(/Proximity to (.+?) \((.+?) km\)/);
      if (match) {
        const template = t.sora?.riskProximityToAirport || "Proximity to {airport} ({distance} km)";
        return template.replace('{airport}', match[1]).replace('{distance}', match[2]);
      }
    }
    
    // Mitigations (risk decreases)
    if (factor.includes('Parachute system reduces')) {
      return t.sora?.mitParachuteReduces || factor;
    }
    if (factor.includes('FTS enables') || factor.includes('controlled flight termination')) {
      return t.sora?.mitFtsControlled || factor;
    }
    if (factor.includes('Geo-awareness prevents')) {
      return t.sora?.mitGeoAwarenessPrevents || factor;
    }
    if (factor.includes('Remote ID enhances')) {
      return t.sora?.mitRemoteIdEnhances || factor;
    }
    
    // Uncertainty factors
    if (factor.includes('Location defined but low precision')) {
      return t.sora?.uncLocationDefined || factor;
    }
    if (factor.includes('No location coordinates')) {
      return t.sora?.uncNoLocationCoordinates || factor;
    }
    
    return factor;
  };

  // ============================================
  // STYLE HELPER FUNCTIONS (extracted ternaries)
  // ============================================
  
  // Get color class based on issue type
  const getIssueColorClass = (issueType) => {
    return issueType === "error" 
      ? "bg-[var(--status-critical)]/10 text-[var(--status-critical)] border-[var(--status-critical)]/30" 
      : "bg-[var(--status-warning)]/10 text-[var(--status-warning)] border-[var(--status-warning)]/30";
  };

  // Get airspace status styling
  const getAirspaceStatusClass = (status) => {
    switch (status) {
      case "high_risk":
        return "bg-[var(--status-critical)]/10 border border-[var(--status-critical)]/30";
      case "caution":
        return "bg-[var(--status-warning)]/10 border border-[var(--status-warning)]/30";
      default:
        return "bg-[var(--status-success)]/10 border border-[var(--status-success)]/30";
    }
  };

  // Get airspace badge styling
  const getAirspaceBadgeClass = (status) => {
    switch (status) {
      case "high_risk":
        return "bg-[var(--status-critical)]/15 text-[var(--status-critical)]";
      case "caution":
        return "bg-[var(--status-warning)]/15 text-[var(--status-warning)]";
      default:
        return "bg-[var(--status-success)]/15 text-[var(--status-success)]";
    }
  };

  // Get zone risk level text styling
  const getZoneRiskTextClass = (riskLevel) => {
    switch (riskLevel) {
      case "high_risk":
        return "text-[var(--status-critical)]";
      case "caution":
        return "text-[var(--status-warning)]";
      default:
        return "";
    }
  };

  // Get confidence label
  const getConfidenceLabel = (confidence) => {
    switch (confidence) {
      case "high":
        return "High";
      case "medium":
        return "Medium";
      default:
        return "Low";
    }
  };

  // Navigate to CheckMate tab and scroll to it
  const navigateToCheckMate = useCallback(() => {
    // Switch to CheckMate tab
    setActiveTab("sora-assistant");
    
    // Use setTimeout to ensure tab switch completes before scrolling
    setTimeout(() => {
      // Find the tab element and scroll to it
      const tabsElement = checkMateTabRef.current;
      if (tabsElement) {
        tabsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  // "Doplnit" from a provisional-iGRC reason: switch to Mission Details, force-expand the target
  // card, scroll to it. Reset to null first so pressing the same target twice in a row still
  // re-triggers the child's forceExpanded effect (a true→true prop change wouldn't). Also reset
  // back to null after the card had a chance to expand — otherwise (2026-09-16 review round 2)
  // OperatorDeclarationsCard/PopulationDensityPreview unmount when the user leaves the Details tab
  // (rendered only under activeTab === "details") and remount with forceExpanded still true on any
  // later return to Details, forcing the card open (and OperatorDeclarationsCard into edit mode)
  // indefinitely, not just right after "Doplnit".
  const navigateToDetailCard = useCallback((target) => {
    setActiveTab("details");
    setForceExpandCard(null);
    requestAnimationFrame(() => setForceExpandCard(target));
    setTimeout(() => {
      const targetRef = target === "operator_declaration" ? operatorDeclarationsRef
        : target === "population_assessment" ? populationDensityRef
        : null;
      targetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    setTimeout(() => setForceExpandCard(null), 600);
  }, []);

  // ProvisionalReason.target ∈ { "aircraft", "population_assessment", "operator_declaration" } —
  // see Fleetora.Application/Common/SoraProvisionalReasonCodes.cs's Targets. A reason's "Doplnit"
  // button is only rendered when isProvisionalTargetActionable(target) is true (below) — this stays
  // a no-op fallback only for a caller that skips that check.
  const navigateToProvisionalTarget = useCallback((target) => {
    if (target === "aircraft") {
      // /aircraft/{id}'s own detail page renders max_speed_ms/max_characteristic_dimension_m
      // read-only (2026-09-16 review round 2) — the only editor is Aircraft.js's modal, opened here
      // via ?edit=<id> (see Aircraft.js's own consuming useEffect).
      if (mission?.aircraft_id) navigate(`/aircraft?edit=${mission.aircraft_id}`);
      return;
    }
    if (target) navigateToDetailCard(target);
  }, [mission?.aircraft_id, navigate, navigateToDetailCard]);

  // Whether a provisional reason's "Doplnit" button would actually do something — false for
  // target === "aircraft" on a mission with no assigned aircraft (2026-09-16 review round 2: the
  // button previously rendered unconditionally and silently did nothing on click in that case).
  const isProvisionalTargetActionable = useCallback((target) =>
    target === "aircraft" ? !!mission?.aircraft_id : !!target,
  [mission?.aircraft_id]);

  // Determine if CheckMate has pending actions (for badge display)
  const hasCheckMateActionNeeded = soraAssessment 
    ? soraAssessment.assessment_confidence !== 'high' && soraAssessment.assessment_confidence !== 'medium'
    : true; // No assessment yet = action needed

  // Get document status badge styling
  const getDocumentStatusClass = (status) => {
    switch (status) {
      case "valid":
        return "bg-[var(--status-success)]/10 text-[var(--status-success)] border-[var(--status-success)]/30";
      case "expiring":
        return "bg-[var(--status-warning)]/10 text-[var(--status-warning)] border-[var(--status-warning)]/30";
      default:
        return "bg-[var(--status-critical)]/10 text-[var(--status-critical)] border-[var(--status-critical)]/30";
    }
  };

  // Get checklist item row styling
  const getChecklistItemClass = (status) => {
    return status === "completed" 
      ? "bg-[var(--status-success)]/5" 
      : "hover:bg-[var(--bg-elevated)]";
  };

  // Get checklist text styling
  const getChecklistTextClass = (status) => {
    return status === "completed" 
      ? "text-[var(--text-muted)] line-through" 
      : "text-[var(--text-primary)]";
  };

  // Get SORA confidence card styling
  const getSoraConfidenceClass = (confidence) => {
    switch (confidence) {
      case "high":
        return "border-[var(--status-success)] text-[var(--status-success)] bg-[var(--status-success)]/5";
      case "medium":
        return "border-[var(--status-warning)] text-[var(--status-warning)] bg-[var(--status-warning)]/5";
      default:
        return "border-[var(--status-critical)] text-[var(--status-critical)] bg-[var(--status-critical)]/5";
    }
  };

  // Check if a field was changed by SORA Assistant
  const getSoraChange = useCallback((fieldName) => {
    const changes = mission?.sora_assistant_changes || [];
    return changes.find(c => c.field === fieldName);
  }, [mission?.sora_assistant_changes]);

  // State for undo confirmation
  const [undoConfirm, setUndoConfirm] = useState(null); // { field, oldValue, label }

  // Dismiss SORA change highlighting
  const dismissSoraChange = async (fieldName) => {
    try {
      await api.patch(
        `/missions/${mission.id}/dismiss-sora-change?field=${fieldName}`,
        {}
      );
      // Refresh mission data
      fetchMissionData();
      toast.success(t.soraAssistant?.changeDismissed || "Change indicator dismissed");
    } catch (err) {
      console.error("Failed to dismiss SORA change:", err);
      toast.error(t.soraAssistant?.dismissError || "Failed to dismiss change");
    }
  };

  // Undo SORA change - restore original value
  const undoSoraChange = async (fieldName) => {
    const change = getSoraChange(fieldName);
    if (!change) return;
    
    try {
      // Restore original value and remove the change record
      await api.patch(
        `/missions/${mission.id}/sora-undo?field=${fieldName}`,
        { original_value: change.old_value }
      );
      
      setUndoConfirm(null);
      await refreshMissionSoraData();
      toast.success(t.soraAssistant?.changeReverted || "Change reverted successfully");
    } catch (err) {
      console.error("Failed to undo SORA change:", err);
      toast.error(getApiErrorMessage(err, language, t.soraAssistant?.undoError || "Failed to revert change"));
    }
  };

  // Get label for field
  const getFieldLabel = (fieldName) => {
    if (fieldName === "max_altitude_m") return t.soraAssistant?.maxAltitudeLabel || "Max. flight altitude";
    if (fieldName === "aircraft_id") return t.soraAssistant?.aircraftLabel || "Aircraft";
    return fieldName;
  };

  // Get SAIL level badge styling
  const getSailLevelClass = (sailLevel) => {
    if (["I", "II"].includes(sailLevel)) {
      return "bg-[var(--status-success)]/15 text-[var(--status-success)]";
    }
    if (["III", "IV"].includes(sailLevel)) {
      return "bg-[var(--status-warning)]/15 text-[var(--status-warning)]";
    }
    return "bg-[var(--status-critical)]/15 text-[var(--status-critical)]";
  };

  // Get GRC level badge styling
  const getGrcLevelClass = (grcNum) => {
    if (grcNum <= 2) {
      return "bg-[var(--status-success)]/15 text-[var(--status-success)]";
    }
    if (grcNum <= 4) {
      return "bg-[var(--status-warning)]/15 text-[var(--status-warning)]";
    }
    return "bg-[var(--status-critical)]/15 text-[var(--status-critical)]";
  };

  // Get ARC level badge styling
  const getArcLevelClass = (arc) => {
    if (arc === "ARC-a" || arc === "ARC-b") {
      return "bg-[var(--status-success)]/15 text-[var(--status-success)]";
    }
    if (arc === "ARC-c") {
      return "bg-[var(--status-warning)]/15 text-[var(--status-warning)]";
    }
    return "bg-[var(--status-critical)]/15 text-[var(--status-critical)]";
  };

  // Get mitigation badge styling
  const getMitigationClass = (present) => {
    return present 
      ? "bg-[var(--status-success)]/10 text-[var(--status-success)]" 
      : "bg-[var(--bg-elevated)] text-[var(--text-muted)]";
  };

  // Get technical mitigation badge styling
  const getTechnicalMitigationClass = (present) => {
    return present 
      ? "bg-[var(--status-success)]/10 text-[var(--text-primary)]" 
      : "bg-[var(--bg-elevated)] text-[var(--text-muted)]";
  };

  // Generate stable key from risk factor text
  const getRiskFactorKey = (item, idx) => {
    return `risk-${item.substring(0, 20).replace(/\s+/g, '-')}-${idx}`;
  };

  // Generate stable key from mitigation
  const getMitigationKey = (mit, idx) => {
    return `mit-${mit.name?.replace(/\s+/g, '-') || idx}-${idx}`;
  };

  // Fetch mission data - wrapped in useCallback to fix stale closure
  // { showLoading: false } (added for refreshMissionSoraData, 2026-09) skips the
  // full-page spinner — the "if (loading) return <spinner>" early return unmounts the
  // whole page including CheckMate, which would flicker/remount on every SORA-relevant
  // mutation otherwise (Codex review).
  const fetchMissionData = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setLoading(true);

      const missionRes = await api.get(`/missions/${missionId}`);
      const missionData = missionRes.data;
      setMission(missionData);
      
      if (missionData.checklist) {
        setChecklist(missionData.checklist);
      }
      
      const expanded = {};
      (missionData.checklist || []).forEach((section, idx) => {
        expanded[idx] = true;
      });
      setExpandedSections(expanded);
      
      // Wrapped in its own try/catch (2026-09, Codex review) — a failure here (e.g.
      // /documents down) must not discard the already-fetched missionData/return value.
      // refreshMissionSoraData relies on that return value to decide whether to refresh
      // the SORA panel; losing it to an unrelated endpoint's outage would silently bring
      // back the exact stale-SAIL bug this session's work fixed. Same pattern as the
      // insurance-check fetch right below, which already tolerates its own failure.
      try {
        const [pilotsRes, aircraftRes, scenariosRes, docsRes] = await Promise.all([
          api.get(`/pilots`),
          api.get(`/aircraft`),
          api.get(`/scenarios`),
          api.get(`/documents`)
        ]);

        const pilotData = pilotsRes.data.find(p => p.id === missionData.pilot_id);
        const aircraftData = aircraftRes.data.find(a => a.id === missionData.aircraft_id);
        const scenarioData = missionData.scenario_id ? scenariosRes.data.find(s => s.id === missionData.scenario_id) : null;

        // Filter approved scenarios for dropdown
        const approvedScenarios = scenariosRes.data.filter(s => s.approval_status === 'approved');
        setScenarios(approvedScenarios);

        setPilot(pilotData);
        setAircraft(aircraftData);
        setScenario(scenarioData);
        setDocuments(docsRes.data);

        // Filter documents linked to this mission
        const linkedDocs = docsRes.data.filter(d =>
          d.mission_id === missionData.id ||
          (d.organization_id === missionData.organization_id &&
           ['Operations Manual', 'ConOps', 'SORA'].includes(d.document_type))
        );
        setMissionDocuments(linkedDocs);
      } catch (err) {
        console.error('Failed to fetch mission-related pilot/aircraft/scenario/document data:', err);
      }

      // Fetch insurance status for this mission
      try {
        const insuranceRes = await api.get(`/missions/${missionId}/insurance-check`);
        setInsuranceStatus(insuranceRes.data);
      } catch (err) {
        setInsuranceStatus(null);
      }

      return missionData;
    } catch (error) {
      toast.error(t.messages?.fetchError || "Failed to fetch mission data");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [missionId,  t.messages]);

  useEffect(() => {
    fetchMissionData();
  }, [fetchMissionData]);

  // Fetch SORA Pre-Assessment (v2 - Enhanced)
  const fetchSoraAssessment = useCallback(async (envType = "rural") => {
    if (!missionId) return;
    
    try {
      setSoraLoading(true);
      const res = await api.get(
        `/sora/preassess/v2/mission/${missionId}?environment_type=${envType}`,
        { }
      );
      setSoraAssessment(res.data);
    } catch (error) {
      setSoraAssessment(null);
    } finally {
      setSoraLoading(false);
    }
  }, [missionId]);

  // Fetch Compliance Detail
  const fetchComplianceDetail = useCallback(async () => {
    if (!missionId) return;
    
    try {
      const res = await api.get(
        `/missions/${missionId}/compliance`,
        { }
      );
      setComplianceDetail(res.data);
    } catch (error) {
      setComplianceDetail(null);
    }
  }, [missionId]);

  // Fetch Airspace Advisory
  const fetchAirspaceAdvisory = useCallback(async () => {
    if (!missionId) return;
    
    try {
      setAirspaceLoading(true);
      const res = await api.get(
        `/airspace/mission/${missionId}`,
        { }
      );
      setAirspaceAdvisory(res.data);
      
      // U-Space zone detection: Check if in CTR or RESTRICTED airspace
      const containingAirspaces = res.data?.containing_airspaces || [];
      const inUSpace = containingAirspaces.some(a => 
        a.type === 'CTR' || a.type === 'RESTRICTED' || a.type === 'TMA'
      );
      setIsInUSpaceZone(inUSpace);
    } catch (error) {
      setAirspaceAdvisory(null);
      setIsInUSpaceZone(false);
    } finally {
      setAirspaceLoading(false);
    }
  }, [missionId]);

  // CheckMate SAIL refresh unification (2026-09): obnoví misi + hlavní SAIL banner +
  // airspace advisory + compliance po jakékoliv SORA-relevantní mutaci (flight_mode,
  // max_altitude_m, aircraft_id, scenario_id, confirmed_equipment, ...). Neobnovuje
  // CheckMate's vlastní optimizationData — to zůstává na onRefreshOptimizations,
  // volaném vždy vedle tohohle callbacku (viz CheckMateDashboard.jsx).
  const refreshMissionSoraData = useCallback(async () => {
    const freshMission = await fetchMissionData({ showLoading: false });
    if (!freshMission) return; // fetchMissionData už zobrazil chybový toast sám
    await Promise.all([
      fetchSoraAssessment(freshMission.environment_type || environmentType),
      fetchAirspaceAdvisory(),
      fetchComplianceDetail(),
    ]);
  }, [fetchMissionData, fetchSoraAssessment, fetchAirspaceAdvisory, fetchComplianceDetail, environmentType]);

  useEffect(() => {
    if (mission?.id) {
      if (mission.environment_type && mission.environment_type !== environmentType) {
        setEnvironmentType(mission.environment_type);
      }
      fetchSoraAssessment(mission.environment_type || environmentType);
      fetchAirspaceAdvisory();
      fetchComplianceDetail();
    }
  }, [mission?.id, mission?.aircraft_id, mission?.environment_type, mission?.latitude, mission?.longitude, environmentType, fetchSoraAssessment, fetchAirspaceAdvisory, fetchComplianceDetail]);

  const updateChecklistItem = (sectionIdx, itemIdx, field, value) => {
    setChecklist(prev => {
      const newChecklist = [...prev];
      newChecklist[sectionIdx] = {
        ...newChecklist[sectionIdx],
        items: [...newChecklist[sectionIdx].items]
      };
      newChecklist[sectionIdx].items[itemIdx] = {
        ...newChecklist[sectionIdx].items[itemIdx],
        [field]: value
      };
      return newChecklist;
    });
  };

  const saveChecklist = async () => {
    try {
      setSaving(true);
      const res = await api.put(
        `/missions/${mission.id}/checklist`,
        checklist,
        { }
      );
      
      setMission(prev => ({
        ...prev,
        checklist: checklist,
        readiness_status: res.data.readiness_status,
        compliance_flag: res.data.compliance_flag,
        lifecycle_status: res.data.lifecycle_status
      }));
      
      toast.success(t.messages.checklistSaved);
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  const markAsComplete = async () => {
    try {
      setMarkingComplete(true);
      const res = await api.post(
        `/missions/${mission.id}/complete`,
        {},
        { }
      );
      
      setMission(prev => ({
        ...prev,
        lifecycle_status: res.data.lifecycle_status || "completed",
        completed_at: res.data.completed_at
      }));
      
      toast.success(t.dailyOps?.missionCompleted || "Mission marked as completed");
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setMarkingComplete(false);
    }
  };

  const generateMissionBrief = async (lang) => {
    try {
      setGeneratingBrief(true);
      const response = await api.get(
        `/missions/${mission.id}/brief?lang=${lang}`,
        { 
          
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mission_brief_${mission.mission_id}_${lang}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === "cs" ? "Briefing vygenerován" : "Mission brief generated");
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setGeneratingBrief(false);
    }
  };

  const generateSoraPdf = async (lang) => {
    try {
      setGeneratingSoraPdf(true);
      const response = await api.get(
        `/missions/${mission.id}/sora-pdf?lang=${lang}`,
        { 
          
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sora_assessment_${mission.mission_id}_${lang}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === "cs" ? "SORA PDF vygenerováno" : "SORA PDF generated");
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setGeneratingSoraPdf(false);
    }
  };

  // Attachment functions
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        await api.post(
          `/missions/${mission.id}/attachments`,
          formData,
          { 
            headers: { 
              
              "Content-Type": "multipart/form-data"
            }
          }
        );
      }
      
      const res = await api.get(`/missions/${mission.id}`);
      setMission(res.data);
      toast.success(t.messages?.fileUploaded || "File uploaded successfully");
    } catch (error) {
      toast.error(t.messages?.uploadError || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await api.get(
        `/missions/${mission.id}/attachments/${attachment.id}`,
        {  responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t.messages?.downloadError || "Download failed");
    }
  };

  const deleteAttachment = async (attachment) => {
    if (!confirm(t.messages?.confirmDelete || "Are you sure you want to delete this file?")) return;
    
    try {
      await api.delete(
        `/missions/${mission.id}/attachments/${attachment.id}`,
        { }
      );
      
      setMission(prev => ({
        ...prev,
        attachments: prev.attachments.filter(a => a.id !== attachment.id)
      }));
      toast.success(t.messages?.fileDeleted || "File deleted");
    } catch (error) {
      toast.error(t.messages?.deleteError || "Delete failed");
    }
  };

  const isImageFile = (mimeType) => {
    return mimeType && mimeType.startsWith("image/");
  };

  const previewAttachment = (attachment) => {
    if (isImageFile(attachment.mime_type)) {
      setPreviewImage(`/missions/${mission.id}/attachments/${attachment.id}`);
    } else {
      downloadAttachment(attachment);
    }
  };

  // Update scenario for mission
  const updateMissionScenario = async (scenarioId) => {
    try {
      setUpdatingScenario(true);
      await api.patch(
        `/missions/${mission.id}`,
        { scenario_id: scenarioId || null },
        { }
      );
      await refreshMissionSoraData();
      toast.success(language === 'cs' ? 'Scénář aktualizován' : 'Scenario updated');
    } catch (error) {
      toast.error(language === 'cs' ? 'Nepodařilo se aktualizovat scénář' : 'Failed to update scenario');
    } finally {
      setUpdatingScenario(false);
    }
  };

  // Create UCL submission from mission
  const createUclSubmission = async () => {
    try {
      setCreatingSubmission(true);
      
      const payload = {
        mission_id: mission.id,
        status: 'draft',
        notes: '',
        document_ids: missionDocuments.map(d => d.id),
        scenario_id: mission.scenario_id || null
      };
      
      await api.post(`/submissions`, payload);
      toast.success(language === 'cs' ? 'Podání vytvořeno' : 'Submission created');
      setShowSubmissionModal(false);
      navigate('/submissions');
    } catch (error) {
      toast.error(language === 'cs' ? 'Nepodařilo se vytvořit podání' : 'Failed to create submission');
    } finally {
      setCreatingSubmission(false);
    }
  };

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const getRelevantDocuments = () => {
    if (!mission) return [];
    const required = ["Operations Manual", "ConOps", "SORA", "Internal Procedures"];
    return documents.filter(d => 
      d.organization_id === mission.organization_id && 
      required.includes(d.document_type)
    );
  };

  // Calculate blocking issues
  const getBlockingIssues = () => {
    const issues = [];
    if (!mission) return issues;
    
    if (!mission.pilot_id) issues.push({ type: "error", icon: User, text: t.dailyOps?.noPilot || "No pilot assigned" });
    if (!mission.aircraft_id) issues.push({ type: "error", icon: Plane, text: t.dailyOps?.noAircraft || "No aircraft assigned" });
    if (!mission.compliance_flag) issues.push({ type: "warning", icon: Shield, text: t.missionStatus?.nonCompliant || "Compliance issues detected" });
    if (mission.readiness_status !== "ready" && mission.lifecycle_status !== "completed") {
      issues.push({ type: "warning", icon: Clock, text: t.missionDetail?.checklistIncomplete || "Checklist incomplete" });
    }
    
    return issues;
  };

  // Get lifecycle status display - AEROWAY colors
  const getLifecycleDisplay = () => {
    const status = mission?.lifecycle_status || "draft";
    const displays = {
      draft: { color: "bg-[var(--text-muted)]/15 text-[var(--text-muted)] border-[var(--text-muted)]/30", label: t.lifecycle?.draft || "Draft", icon: Clock },
      ready: { color: "bg-[var(--status-success)]/15 text-[var(--status-success)] border-[var(--status-success)]/30", label: t.lifecycle?.ready || "Ready", icon: CheckCircle2 },
      active: { color: "bg-[var(--status-active)]/15 text-[var(--status-active)] border-[var(--status-active)]/30", label: t.lifecycle?.active || "Active", icon: PlayCircle },
      completed: { color: "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] border-[var(--brand-accent)]/30", label: t.lifecycle?.completed || "Completed", icon: CheckCircle2 }
    };
    return displays[status] || displays.draft;
  };

  // Calculate checklist progress
  const getChecklistProgress = () => {
    let total = 0;
    let completed = 0;
    checklist.forEach(section => {
      section.items.forEach(item => {
        total++;
        if (item.status === "completed") completed++;
      });
    });
    return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  // Take-off point + flight area summary for the read-only map (mission-map unification, PR6) — the
  // same server validation the mission dialog uses (length/area, take-off status, legacy problems).
  const flightAreaSettings = useFlightAreaSettings();
  const flightAreaValidation = useFlightAreaValidation({
    flightArea: mission?.flight_area,
    corridorWidthM: mission?.corridor_width_m,
    circleRadiusM: mission?.circle_radius_m,
    latitude: mission?.latitude,
    longitude: mission?.longitude,
    enabled: !!mission,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[var(--brand-accent)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="text-center py-16">
        <FileText className="h-16 w-16 text-[var(--text-muted)] mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-[var(--text-primary)]">{t.flightOps.missionNotFound}</h2>
        <Button 
          onClick={() => navigate("/missions")} 
          variant="outline" 
          className="mt-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" /> {t.flightOps.backToMissions}
        </Button>
      </div>
    );
  }

  const blockingIssues = getBlockingIssues();
  const lifecycleDisplay = getLifecycleDisplay();
  const checklistProgress = getChecklistProgress();
  const LifecycleIcon = lifecycleDisplay.icon;

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="mission-detail-page">
      {/* ============================================
          HEADER SECTION
          Mission ID, Status, Compliance, Actions
          ============================================ */}
      <div className="bg-[var(--card-bg)] rounded-xl border border-[var(--card-border)] p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: Back + Title + Status */}
          <div className="min-w-0 flex-1">
            <Button 
              onClick={() => navigate("/missions")} 
              variant="ghost" 
              className="gap-1.5 mb-2 -ml-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] h-8 text-sm"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {t.flightOps.backToMissions}
            </Button>
            <div className="flex flex-wrap items-center gap-2 lg:gap-3">
              <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-[var(--text-primary)] truncate">
                {mission.mission_id || t.flightOps.missionDetail}
              </h1>
              <Badge className={`${lifecycleDisplay.color} gap-1 px-2 lg:px-3 py-0.5 lg:py-1 flex-shrink-0 text-xs lg:text-sm`}>
                <LifecycleIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
                {lifecycleDisplay.label}
              </Badge>
              {/* U-Space Zone Badge */}
              {isInUSpaceZone && (
                <Badge className="bg-blue-100 text-blue-800 gap-1 px-2 lg:px-3 py-0.5 lg:py-1 flex-shrink-0 text-xs lg:text-sm" data-testid="uspace-zone-badge">
                  <Radar className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
                  {t.uSpace?.uSpaceZone || "U-Space Zone"}
                </Badge>
              )}
            </div>
            
            {/* Quick Info Row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 flex-shrink-0 text-[var(--brand-accent)]" />
                {formatIsoDateDmy(mission.mission_date)}
              </span>
              <span className="flex items-center gap-1.5 min-w-0">
                <MapPin className="h-4 w-4 flex-shrink-0 text-[var(--brand-accent)]" />
                <span className="truncate max-w-[250px]">{mission.location}</span>
              </span>
              {pilot && (
                <span className="flex items-center gap-1.5">
                  <User className="h-4 w-4 flex-shrink-0 text-[var(--brand-accent)]" />
                  {pilot.name}
                </span>
              )}
              {aircraft && (
                <span className="flex items-center gap-1.5">
                  <Plane className="h-4 w-4 flex-shrink-0 text-[var(--brand-accent)]" />
                  {aircraft.model}
                </span>
              )}
            </div>
          </div>
          
          {/* Right: Actions */}
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              className="gap-1.5 h-9 px-3 text-sm"
              onClick={() => navigate('/missions', { state: editFromDetailState(mission.id) })}
            >
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">{t.actions?.edit || "Edit"}</span>
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5 h-9 px-3 text-sm" disabled={generatingBrief} data-testid="generate-brief-btn">
                  {generatingBrief ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">{t.flightOps.generateBrief}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[var(--card-bg)] border-[var(--card-border)]">
                <DropdownMenuItem onClick={() => generateMissionBrief("en")} className="text-[var(--text-primary)] focus:bg-[var(--bg-elevated)]" data-testid="brief-english-btn">
                  <Globe className="h-4 w-4 mr-2" /> English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => generateMissionBrief("cs")} className="text-[var(--text-primary)] focus:bg-[var(--bg-elevated)]">
                  <Globe className="h-4 w-4 mr-2" /> Čeština
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5 h-9 px-3 text-sm" disabled={generatingSoraPdf} data-testid="export-sora-pdf-btn">
                  {generatingSoraPdf ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">{t.sora?.exportPdf || "Export SORA PDF"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[var(--card-bg)] border-[var(--card-border)]">
                <DropdownMenuItem onClick={() => generateSoraPdf("en")} className="text-[var(--text-primary)] focus:bg-[var(--bg-elevated)]">
                  <Globe className="h-4 w-4 mr-2" /> English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => generateSoraPdf("cs")} className="text-[var(--text-primary)] focus:bg-[var(--bg-elevated)]">
                  <Globe className="h-4 w-4 mr-2" /> Čeština
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {["ready", "active"].includes(mission.lifecycle_status) && (
              <Button
                className="gap-1.5 bg-[var(--status-success)] hover:bg-[var(--status-success)]/90 h-9 px-3 text-sm text-white"
                onClick={markAsComplete}
                disabled={markingComplete}
              >
                {markingComplete ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">{t.dailyOps?.markCompleted || "Mark Complete"}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ============================================
          INSURANCE WARNING BANNER
          ============================================ */}
      {insuranceStatus && (!insuranceStatus.can_approve || insuranceStatus.warnings?.length > 0) && mission.lifecycle_status !== "completed" && (
        <Card className={`border-l-4 ${!insuranceStatus.can_approve ? 'border-l-[var(--status-critical)] border-[var(--status-critical)]/30 bg-[var(--status-critical)]/5' : 'border-l-[var(--status-warning)] border-[var(--status-warning)]/30 bg-[var(--status-warning)]/5'}`}>
          <CardContent className="py-3 lg:py-4 px-4 lg:px-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <Shield className={`h-5 w-5 flex-shrink-0 mt-0.5 ${!insuranceStatus.can_approve ? 'text-[var(--status-critical)]' : 'text-[var(--status-warning)]'}`} />
              <div className="flex-1 min-w-0">
                <p className={`font-semibold mb-1 ${!insuranceStatus.can_approve ? 'text-[var(--status-critical)]' : 'text-[var(--status-warning)]'}`}>
                  {!insuranceStatus.can_approve 
                    ? (language === 'cs' ? 'Pojištění neplatné - mise nelze schválit' : 'Insurance invalid - mission cannot be approved')
                    : (language === 'cs' ? 'Upozornění na pojištění' : 'Insurance Warning')
                  }
                </p>
                <div className="flex flex-wrap gap-2">
                  {insuranceStatus.warnings?.map((warning, idx) => (
                    <Badge 
                      key={`ins-warning-${idx}`} 
                      variant="outline" 
                      className={`gap-1 text-xs ${!insuranceStatus.can_approve ? 'border-[var(--status-critical)] text-[var(--status-critical)]' : 'border-[var(--status-warning)] text-[var(--status-warning)]'}`}
                    >
                      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{warning}</span>
                    </Badge>
                  ))}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className={`flex-shrink-0 ${!insuranceStatus.can_approve ? 'border-[var(--status-critical)] text-[var(--status-critical)] hover:bg-[var(--status-critical)]/10' : 'border-[var(--status-warning)] text-[var(--status-warning)] hover:bg-[var(--status-warning)]/10'}`}
                onClick={() => navigate('/insurance')}
              >
                <Shield className="h-4 w-4 mr-1" />
                {language === 'cs' ? 'Spravovat pojištění' : 'Manage Insurance'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================================
          BLOCKING ISSUES ALERT
          ============================================ */}
      {blockingIssues.length > 0 && mission.lifecycle_status !== "completed" && (
        <Card className="border-[var(--status-warning)]/30 bg-[var(--status-warning)]/5">
          <CardContent className="py-3 lg:py-4 px-4 lg:px-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <AlertOctagon className="h-5 w-5 text-[var(--status-warning)] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--status-warning)] mb-2">
                  {t.missionDetail?.blockedBy || "This mission cannot be marked ready due to:"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {blockingIssues.map((issue, idx) => (
                    <Badge 
                      key={`blocking-${issue.text.substring(0, 15).replace(/\s+/g, '-')}-${idx}`} 
                      variant="outline" 
                      className={`gap-1 text-xs ${getIssueColorClass(issue.type)}`}
                    >
                      <issue.icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{issue.text}</span>
                    </Badge>
                  ))}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-[var(--status-warning)] text-[var(--status-warning)] hover:bg-[var(--status-warning)]/10 flex-shrink-0"
                onClick={() => navigate('/missions', { state: editFromDetailState(mission.id) })}
              >
                <Pencil className="h-4 w-4 mr-1" />
                {t.missionDetail?.fixIssues || "Fix Issues"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================================
          MISSION STATUS OVERVIEW - SAIL Badge + Scenario
          ============================================ */}
      <Card className="border-[var(--border-default)]" data-testid="mission-status-overview">
        <CardContent className="py-4 px-4 lg:px-6">
          <div className="flex flex-wrap items-center gap-4 lg:gap-6">
            {/* SAIL Badge */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[var(--text-muted)]">
                {language === 'cs' ? 'SAIL úroveň' : 'SAIL Level'}:
              </span>
              {soraAssessment ? (() => {
                const sailLevelStr = soraAssessment.estimated_sail_level || soraAssessment.sail_level || '';
                const isOutOfScope = sailLevelStr.toUpperCase() === 'OUT_OF_SCOPE';
                const provisionalWarning = soraAssessment.provisional_result
                  && hasSailWarningReason(soraAssessment.provisional_reasons) && (
                  // A `title` prop on lucide's <svg> becomes an SVG attribute, not a <title> child
                  // element — browsers only show a tooltip from the latter, so this needs a
                  // title-bearing wrapper (2026-09-16 review round 2). Tooltip text is the actual
                  // reason (e.g. "determined from a CGA/gathering declaration, not population
                  // density"), not a generic "Preliminary estimate" label (2026-09-16, user request).
                  <span title={sailWarningTooltip(soraAssessment.provisional_reasons, language)}>
                    <AlertTriangle className="h-3 w-3" />
                  </span>
                );
                if (isOutOfScope) {
                  return (
                    <Badge className="bg-red-100 text-red-700 border-red-300 font-bold px-3 py-1 gap-1.5" data-testid="sail-badge">
                      {language === 'cs' ? 'Mimo SORA' : 'Out of SORA'}
                      {provisionalWarning}
                    </Badge>
                  );
                }
                const romanToNum = { 'I': 1, 'II': 2, 'III': 3, 'IV': 4, 'V': 5, 'VI': 6 };
                const cleanedStr = sailLevelStr.toUpperCase().replace(/SAIL\s*/i, '').trim();
                const sailLevel = romanToNum[cleanedStr] || parseInt(sailLevelStr.replace(/[^0-9]/g, '') || '0');
                const sailColor = sailLevel <= 2 ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                : sailLevel <= 4 ? 'bg-amber-100 text-amber-700 border-amber-300'
                                : 'bg-red-100 text-red-700 border-red-300';
                return (
                  <Badge className={`${sailColor} font-bold px-3 py-1 gap-1.5`} data-testid="sail-badge">
                    SAIL {sailLevel > 0 ? sailLevel : '?'}
                    {provisionalWarning}
                  </Badge>
                );
              })() : (
                <Badge className="bg-slate-100 text-slate-600 border-slate-300 px-3 py-1" data-testid="sail-badge-not-assessed">
                  {language === 'cs' ? 'Neohodnoceno' : 'Not assessed'}
                </Badge>
              )}
            </div>
            
            {/* Scenario Selector */}
            <div className="flex items-center gap-3 flex-1 min-w-[250px]">
              <span className="text-sm font-medium text-[var(--text-muted)]">
                {language === 'cs' ? 'Scénář' : 'Scenario'}:
              </span>
              <Select
                value={mission.scenario_id || "__none__"}
                onValueChange={(value) => updateMissionScenario(value === "__none__" ? null : value)}
                disabled={updatingScenario}
              >
                <SelectTrigger className="w-[280px] h-9" data-testid="scenario-selector">
                  <SelectValue placeholder={language === 'cs' ? 'Vyberte scénář' : 'Select scenario'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{language === 'cs' ? 'Bez scénáře' : 'No scenario'}</SelectItem>
                  {scenarios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                          {s.scenario_type}
                        </Badge>
                        <span>{s.scenario_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scenario && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                  {scenario.scenario_type}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============================================
          2 INDEPENDENT SECTIONS: Documents, UCL Submission
          (CheckMate moved to dedicated tab with SAIL banner CTA)
          ============================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mission-sections">
        
        {/* Section 1: Documents */}
        <Card className="border-[var(--border-default)]" data-testid="documents-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              {language === 'cs' ? 'Dokumenty' : 'Documents'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Document count */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">
                  {language === 'cs' ? 'Připojené dokumenty' : 'Attached documents'}:
                </span>
                <Badge variant="outline">{missionDocuments.length}</Badge>
              </div>
              
              {/* Document list (compact) */}
              {missionDocuments.length > 0 && (
                <div className="space-y-1 max-h-[100px] overflow-y-auto">
                  {missionDocuments.slice(0, 3).map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                      <FileText className="h-3 w-3" />
                      <span className="truncate">{doc.document_name || doc.document_type}</span>
                    </div>
                  ))}
                  {missionDocuments.length > 3 && (
                    <span className="text-xs text-[var(--text-muted)]">
                      +{missionDocuments.length - 3} {language === 'cs' ? 'dalších' : 'more'}
                    </span>
                  )}
                </div>
              )}
              
              {/* Action buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={() => navigate('/documents')}
                >
                  <FileUp className="h-3.5 w-3.5" />
                  {language === 'cs' ? 'Nahrát vlastní' : 'Upload own'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={navigateToCheckMate}
                  data-testid="generate-ai-docs-btn"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {language === 'cs' ? 'Generovat AI' : 'Generate AI'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: UCL Submission */}
        <Card className="border-[var(--border-default)]" data-testid="ucl-submission-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Send className="h-4 w-4 text-indigo-600" />
              {language === 'cs' ? 'Podání ÚCL' : 'UCL Submission'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-muted)]">
                {language === 'cs' 
                  ? 'Vytvořte podání k Úřadu civilního letectví' 
                  : 'Create submission to Civil Aviation Authority'}
              </p>
              
              {/* Pre-fill info */}
              <div className="text-xs text-[var(--text-secondary)] space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  <span>{language === 'cs' ? 'Mise' : 'Mission'}: {mission.mission_id}</span>
                </div>
                {scenario && (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    <span>{language === 'cs' ? 'Scénář' : 'Scenario'}: {scenario.scenario_type}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  <span>{missionDocuments.length} {language === 'cs' ? 'dokumentů' : 'documents'}</span>
                </div>
              </div>
              
              <Button
                className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                size="sm"
                onClick={() => setShowSubmissionModal(true)}
                data-testid="create-ucl-submission-btn"
              >
                <Send className="h-3.5 w-3.5" />
                {language === 'cs' ? 'Vytvořit podání' : 'Create submission'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SORA/SAIL Status Banner - Only show when Mission Details tab is active */}
      {activeTab === "details" && soraAssessment && (() => {
        // Extract SAIL level - try multiple possible field names
        const sailLevelStr = soraAssessment.estimated_sail_level || soraAssessment.sail_level || '';
        const isOutOfScope = sailLevelStr.toUpperCase() === 'OUT_OF_SCOPE';
        const provisionalResult = !!soraAssessment.provisional_result;
        const provisionalReasons = soraAssessment.provisional_reasons || [];

        // Convert Roman numerals to numbers
        const romanToNum = { 'I': 1, 'II': 2, 'III': 3, 'IV': 4, 'V': 5, 'VI': 6 };
        const cleanedStr = sailLevelStr.toUpperCase().replace(/SAIL\s*/i, '').trim();
        const sailLevel = isOutOfScope ? 0 : (romanToNum[cleanedStr] || parseInt(sailLevelStr.replace(/[^0-9]/g, '') || '0'));

        const sailCategory = isOutOfScope ? 'outOfScope' : sailLevel <= 0 ? 'unknown' : sailLevel <= 2 ? 'low' : sailLevel <= 4 ? 'medium' : 'high';

        // Use correct field names from API response
        const grcLevel = soraAssessment.ground_risk_class_full || soraAssessment.grc_level || '';
        const arcLevel = soraAssessment.air_risk_class || soraAssessment.arc_level || '';
        // "GRC-OUT-OF-SCOPE" (SoraGrcMethods.OutOfScopeGrc) is a wire token, not a user-facing label
        // — 2026-09-16 review round 2 caught it leaking into the metrics chip below unformatted.
        const grcDisplay = grcLevel.toUpperCase() === 'GRC-OUT-OF-SCOPE'
          ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA')
          : (grcLevel || 'GRC-?');

        // Color schemes based on SAIL level
        const colors = {
          unknown: { badge: '#6B7280', bg: 'rgba(107, 114, 128, 0.05)', border: '#6B7280', text: '#374151' },
          low: { badge: '#3B9E5A', bg: 'rgba(59, 158, 90, 0.05)', border: '#3B9E5A', text: '#1E5631' },
          medium: { badge: '#C89830', bg: 'rgba(200, 152, 48, 0.05)', border: '#C89830', text: '#7A5C1E' },
          high: { badge: '#A8391F', bg: 'rgba(168, 57, 31, 0.05)', border: '#A8391F', text: '#6B2415' },
          // Mission's intrinsic GRC exceeds Table 2/7's scope — Category C (Certified), not a Specific
          // SAIL, see SailLevel.OutOfScope.
          outOfScope: { badge: '#A8391F', bg: 'rgba(168, 57, 31, 0.08)', border: '#A8391F', text: '#6B2415' },
        };
        const c = colors[sailCategory];
        
        // CheckMate SAIL fix (2026-09): soraAssessment has no top-level environment_type/operation_type
        // or airspace_info — those live nested under assessment_inputs/arc_details. mission.flight_type
        // doesn't exist at all (real field is flight_mode, and the effective — post flight_mode-override
        // — value is what assessment_inputs.operation_type reflects).
        const effectiveEnvironment = soraAssessment.assessment_inputs?.environment_type?.toLowerCase();
        const effectiveFlightMode = soraAssessment.assessment_inputs?.operation_type?.toUpperCase();

        // Build reasons for SAIL level
        const reasons = [];
        // Figure 6 ARC: the undetermined airspace or the concrete high-risk node; null for the legacy
        // airport-proximity ARC, which keeps the generic reason.
        const arcReason = arcBannerReason(soraAssessment.arc_details, t);
        if (arcReason) {
          reasons.push(arcReason);
        } else if (soraAssessment.arc_details?.airspace_status === 'high_risk') {
          reasons.push(t.sailBanner?.elevatedAirspaceRisk || "Elevated airspace risk");
        }
        const arcNoteLines = arcNotes(soraAssessment.arc_details, t);
        if (soraAssessment.arc_details?.airport_name) {
          reasons.push(`${t.sailBanner?.nearAirport || "Near airport"} (${soraAssessment.arc_details.airport_proximity_km?.toFixed(1) || '?'}km)`);
        }
        if (effectiveEnvironment === 'urban') {
          reasons.push(t.sailBanner?.urbanArea || "Urban / populated area");
        }
        if (effectiveFlightMode === 'BVLOS') {
          reasons.push(t.sailBanner?.bvlosOperation || "BVLOS operation");
        }
        if (grcLevel && parseInt(grcLevel.replace(/[^0-9]/g, '')) >= 3) {
          reasons.push(`${t.sailBanner?.highGrc || "High ground risk"} (${grcLevel})`);
        }
        
        return (
          <Card 
            className="mb-4 overflow-hidden"
            style={{ 
              background: c.bg,
              borderLeft: `4px solid ${c.border}`,
              borderColor: `${c.border}20`
            }}
            data-testid="sail-status-banner"
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Left: SAIL Badge + Metrics + Reasons */}
                <div className="flex-1 space-y-3">
                  {/* Row 1: SAIL Badge + Category Label + Metrics */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Large SAIL Badge */}
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-white shadow-sm"
                      style={{ background: c.badge }}
                    >
                      <span className="text-lg">
                        {isOutOfScope ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA') : `SAIL ${sailLevel > 0 ? sailLevel : (sailLevelStr || '?')}`}
                      </span>
                    </div>

                    {/* Category Label */}
                    <span className="font-semibold text-base" style={{ color: c.text }}>
                      {isOutOfScope
                        ? (language === 'cs' ? 'Mimo rozsah SORA (Category C)' : 'Out of SORA scope (Category C)')
                        : sailLevel <= 0
                        ? (t.sailBanner?.calculating || "Calculating...")
                        : sailLevel <= 2
                        ? (t.sailBanner?.openCategory || "Open category")
                        : (t.sailBanner?.specificAuthorization || "Specific authorization")}
                    </span>
                    
                    {/* Compact Metrics */}
                    <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--text-muted)]">
                      <span className="px-2 py-0.5 bg-[var(--bg-elevated)] rounded border border-[var(--border-default)]">
                        {grcDisplay}
                      </span>
                      <span className="px-2 py-0.5 bg-[var(--bg-elevated)] rounded border border-[var(--border-default)]">
                        {arcLevel || 'ARC-?'}
                      </span>
                      <span className="px-2 py-0.5 bg-[var(--bg-elevated)] rounded border border-[var(--border-default)]">
                        {effectiveEnvironment === 'urban'
                          ? (t.sora?.urban || 'Urban')
                          : (t.sora?.rural || 'Rural')}
                        {/* Manual EASA CENSUS/LUISA assessment, when saved — display only, does not
                            itself feed this SAIL/GRC calculation (see decisions.md). */}
                        {populationAssessment?.maximum != null &&
                          ` - ${new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-GB', { maximumFractionDigits: 2 }).format(populationAssessment.maximum)} ${language === 'cs' ? 'osob/km²' : 'persons/km²'}`}
                      </span>
                      {mission.max_altitude_m && (
                        <span className="px-2 py-0.5 bg-[var(--bg-elevated)] rounded border border-[var(--border-default)]">
                          {mission.max_altitude_m}m
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Row 2: Why SAIL X reasons */}
                  {reasons.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                        {t.sailBanner?.whySailLevel || "Why SAIL"} {sailLevel > 0 ? sailLevel : ''}:
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {reasons.slice(0, 3).map((reason, idx) => (
                          <span key={idx} className="text-sm text-[var(--text-secondary)] flex items-center gap-1.5">
                            <span style={{ color: c.badge }}>→</span> {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Figure 6 notes: coordination with ATS in a CTR/MCTR, ATC agreement in ARC-d, TMPR of BVLOS */}
                  {arcNoteLines.length > 0 && (
                    <div className="space-y-1" data-testid="sail-arc-notes">
                      {arcNoteLines.map((note, idx) => (
                        <p key={idx} className="text-xs text-[var(--text-secondary)] flex items-start gap-1.5">
                          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: c.badge }} />
                          <span>{note}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Předběžný odhad (Table 2 iGRC provisional) — always shown when provisional_result
                      is true. Reasons split into two groups (2026-09-16, live user testing): a
                      reason with a target is something the user can act on ("Doplnit" CTA); a reason
                      with target=null (manual_footprint, automatic_grc1_exemption) is a permanent
                      property of the estimation method itself, not missing data. ONE box, not two —
                      an empty-looking amber "action needed" box with nothing actionable inside it
                      (all-info case) was itself the follow-up bug report; color/urgency now follows
                      whether there's anything to actually press, not just provisional_result. */}
                  {/* One box per reason group (undetermined airspace, ARC-a declaration, simplified GRC
                      calculation), each with its own heading; the undetermined airspace and an ignored
                      declaration are warnings even with nothing to complete. */}
                  {provisionalResult && (provisionalReasons.length > 0
                    ? groupProvisionalReasons(provisionalReasons)
                    : [{ group: 'grc', reasons: [] }]).map(({ group, reasons: groupReasons }) => {
                    const actionableReasons = groupReasons.filter(r => isProvisionalTargetActionable(r.target));
                    const infoReasons = groupReasons.filter(r => !isProvisionalTargetActionable(r.target));
                    const hasActionable = actionableReasons.length > 0;
                    const isWarning = hasActionable || isWarningGroup(group, groupReasons);
                    const color = isWarning
                      ? { border: '#C89830', bg: 'rgba(200, 152, 48, 0.08)', icon: '#C89830', text: '#7A5C1E' }
                      : { border: '#CBD5E1', bg: 'rgba(148, 163, 184, 0.08)', icon: '#64748B', text: '#475569' };
                    return (
                      <div
                        key={group}
                        className="rounded-md border p-3 space-y-2"
                        style={{ borderColor: color.border, background: color.bg }}
                        data-testid={group === 'grc' ? "sail-provisional-block" : `sail-provisional-block-${group}`}
                      >
                        <div className="flex items-center gap-2">
                          {isWarning
                            ? <AlertTriangle className="h-4 w-4 flex-shrink-0" style={{ color: color.icon }} />
                            : <Info className="h-4 w-4 flex-shrink-0" style={{ color: color.icon }} />}
                          <span className="text-sm font-semibold" style={{ color: color.text }}>
                            {provisionalGroupTitle(group, groupReasons, t)}
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {actionableReasons.map((reason, idx) => (
                            <li
                              key={reason.code || idx}
                              className="flex items-start justify-between gap-2 text-sm"
                              style={{ color: color.text }}
                            >
                              {/* min-w-0 lets long text wrap within its own space instead of pushing
                                  the button onto its own (left-aligned) flex-wrap line — 2026-09-16,
                                  live user testing with 2 simultaneous reasons. */}
                              <span className="min-w-0 flex-1">{localizeReasonMessage(reason, language)}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs flex-shrink-0"
                                onClick={() => navigateToProvisionalTarget(reason.target)}
                              >
                                {language === 'cs' ? 'Doplnit' : 'Complete'}
                              </Button>
                            </li>
                          ))}
                          {infoReasons.map((reason, idx) => (
                            <li key={reason.code || idx} className="text-sm" style={{ color: color.text }}>
                              {localizeReasonMessage(reason, language)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}

                  {/* Row 3: Status Line */}
                  <div className="flex items-center gap-2 pt-1">
                    {isOutOfScope ? (
                      <>
                        <AlertTriangle className="h-4 w-4" style={{ color: '#A8391F' }} />
                        <span className="text-sm font-medium" style={{ color: '#A8391F' }}>
                          {language === 'cs'
                            ? 'Mimo rozsah SORA Specific — vyžaduje kategorii Certified'
                            : 'Outside SORA Specific scope — requires the Certified category'}
                        </span>
                      </>
                    ) : sailLevel <= 0 ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" style={{ color: '#6B7280' }} />
                        <span className="text-sm font-medium" style={{ color: '#6B7280' }}>
                          {t.sailBanner?.assessmentInProgress || "Assessment in progress..."}
                        </span>
                      </>
                    ) : sailLevel <= 2 ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" style={{ color: '#3B9E5A' }} />
                        <span className="text-sm font-medium" style={{ color: '#3B9E5A' }}>
                          {t.sailBanner?.openCategoryStatus || "Open category / STS scenario"}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-4 w-4" style={{ color: '#C89830' }} />
                        <span className="text-sm font-medium" style={{ color: '#C89830' }}>
                          {t.sailBanner?.specificRequired || "Requires CAA Specific authorization"}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Right: CTA Button with Action Needed badge */}
                <div className="flex-shrink-0 flex flex-col items-end gap-2">
                  {hasCheckMateActionNeeded && (
                    <Badge 
                      variant="outline" 
                      className="bg-amber-50 text-amber-700 border-amber-300 text-xs"
                    >
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {language === 'cs' ? 'Akce nutná' : 'Action required'}
                    </Badge>
                  )}
                  <Button
                    className="gap-2 h-10 px-5 text-sm font-medium shadow-sm hover:shadow-md transition-all"
                    style={{ background: '#1E3A5F', color: 'white' }}
                    onClick={navigateToCheckMate}
                    data-testid="sail-banner-checkmate-btn"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t.sailBanner?.openCheckMate || "Open CheckMate"} →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Ground risk / population inputs — grouped together since both feed the same SORA GRC concept:
          OperatorDeclarationsCard is the persisted, SORA-connected override; PopulationDensityPreview is
          the manual CENSUS/LUISA reference assessment below it (persisted since 2026-09-14, but not yet
          wired into any SORA/GRC calculation — see docs/project/decisions.md). Moved here from the
          Operations Planning/map card per 2026-09-14 review recommendation. The ARC-a declaration card
          between them is an air-risk input, kept next to the other operator declaration. */}
      {activeTab === "details" && mission && (
        <>
          <div ref={operatorDeclarationsRef}>
            <OperatorDeclarationsCard
              mission={mission}
              onUpdate={refreshMissionSoraData}
              language={language}
              t={t.operatorDeclarations}
              forceExpanded={forceExpandCard === "operator_declaration"}
            />
          </div>
          <AtypicalAirspaceDeclarationCard
            mission={mission}
            onUpdate={refreshMissionSoraData}
            evaluationReasons={soraAssessment?.provisional_reasons}
            currentUserId={user?.id}
            language={language}
            t={t.atypicalDeclaration}
          />
          <div ref={populationDensityRef}>
            <PopulationDensityPreview
              mission={mission}
              onAssessmentChange={setPopulationAssessment}
              onSaved={refreshMissionSoraData}
              forceExpanded={forceExpandCard === "population_assessment"}
            />
          </div>
        </>
      )}

      {/* Wave 2.5: Declaration Override Modal */}
      <DeclarationOverrideModal
        isOpen={showDeclarationModal}
        onClose={() => setShowDeclarationModal(false)}
        mission={mission}
        soraClassification={mission?.sora_classification || soraAssessment}
        onUpdate={fetchMissionData}
        language={language}
      />

      {/* Tabs for Mission Details and CheckMate */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value)} className="w-full" ref={checkMateTabRef}>
        {/* CheckMate's tab needs more room than the plain icon+word tabs — it carries a title, a
            status badge (dot + "Akce nutná"/"Action needed" or similar) AND a subtitle line, which
            an equal 1/5 share cut off (2026-09-16, live user testing screenshot). */}
        <TabsList className="grid w-full max-w-4xl grid-cols-2 lg:grid-cols-[repeat(3,1fr)_1.6fr] gap-1 lg:gap-0 mb-4 h-auto p-1">
          {/* Mission Details Tab */}
          <TabsTrigger 
            value="details" 
            className="flex items-center gap-2 py-2.5 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-mission-details"
          >
            <FileText className="h-4 w-4" />
            <span>{t.flightOps?.missionDetails || "Mission Details"}</span>
          </TabsTrigger>
          
          {/* Wave 3 Block 3A: Contacts Tab */}
          <TabsTrigger 
            value="contacts" 
            className="flex items-center gap-2 py-2.5 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-mission-contacts"
          >
            <Users className="h-4 w-4" />
            <span>{language === 'cs' ? 'Kontakty' : 'Contacts'}</span>
          </TabsTrigger>
          
          {/* Sprint Y Block E: Correspondence Tab */}
          <TabsTrigger 
            value="correspondence" 
            className="flex items-center gap-2 py-2.5 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-mission-correspondence"
          >
            <Mail className="h-4 w-4" />
            <span>{language === 'cs' ? 'Korespondence' : 'Correspondence'}</span>
          </TabsTrigger>
          
          {/* CheckMate Tab - Enhanced with pulsing indicator */}
          <TabsTrigger 
            value="sora-assistant" 
            className="relative col-span-2 lg:col-span-1 flex flex-col items-center gap-0.5 py-2 px-4 
                       data-[state=inactive]:bg-gradient-to-r data-[state=inactive]:from-[#1E3A5F]/8 data-[state=inactive]:to-[#3B5998]/12
                       data-[state=inactive]:border data-[state=inactive]:border-[#1E3A5F]/20
                       data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white data-[state=active]:shadow-md
                       transition-all duration-300 hover:scale-[1.02]"
            data-testid="tab-sora-assistant"
          >
            {/* Dot indicator when inactive */}
            <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-[#1E3A5F] data-[state=active]:hidden" />
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="font-semibold">{t.soraAssistant?.title || "CheckMate"}</span>
              {/* Status Badge */}
              {soraAssessment && (() => {
                // 2026-09-16, live user testing: "3 kroky"/"3 steps" was a hardcoded literal, not
                // derived from anything — could show even when the only provisional_reasons entry
                // (e.g. manual_footprint) has nothing the user can actually act on. Count only
                // actionable reasons (same isProvisionalTargetActionable check as the banner's
                // "Doplnit" buttons). A "medium" result with zero actionable reasons (e.g. a
                // fully-filled Table 2 manual-footprint assessment, permanently capped at "medium" —
                // see SoraHeuristicEngine.cs's Table 2 confidence cap) has nothing for the user to
                // do, so per user decision (2026-09-16) show no badge at all rather than a
                // misleading step count or a vague "preliminary" label.
                const actionableCount = (soraAssessment.provisional_reasons || [])
                  .filter(r => isProvisionalTargetActionable(r.target)).length;
                if (soraAssessment.assessment_confidence === 'medium' && actionableCount === 0) return null;
                return (
                  <span
                    className={`ml-1 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                      soraAssessment.assessment_confidence === 'high'
                        ? 'bg-green-500/20 text-green-700 dark:text-green-400'
                        : soraAssessment.assessment_confidence === 'medium'
                        ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
                        : 'bg-red-500/20 text-red-700 dark:text-red-400'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {soraAssessment.assessment_confidence === 'high'
                      ? (t.soraAssistant?.statusReady || 'Ready')
                      : soraAssessment.assessment_confidence === 'medium'
                      ? `${actionableCount} ${t.soraAssistant?.statusStepsRemaining || 'steps'}`
                      : (t.soraAssistant?.statusActionNeeded || 'Action needed')
                    }
                  </span>
                );
              })()}
            </div>
            <span className="text-[10px] opacity-70 data-[state=active]:opacity-90">
              {t.soraAssistant?.tabSubtitle || "AI authorization guide"}
            </span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="details">
          {/* Main Content Grid — Operations Planning full width, three cards below it */}
          <div className="grid grid-cols-1 gap-4 xl:gap-6">

        {/* Operations Planning Panel */}
        <div>
          {(() => {
            // Mission-map unification (PR6): the card shows the one mission map read-only — take-off
            // point (drone marker), flight area / route + corridor / circle, summary and coordinate table.
            // A legacy mission without a take-off point or without an area still gets the map when it
            // has the other one, plus a hint to complete it in the mission editor.
            const hasTakeoff = Number.isFinite(mission.latitude) && Number.isFinite(mission.longitude);
            const areaKind = classifyFlightArea(mission.flight_area).kind;
            // Same rule as the edit dialog's completed-mission lock exception (lib/flightArea.js): no
            // point, no/unusable area, a route without a valid width, or stored data the server rejects.
            const needsFix = storedFlightAreaNeedsFix({
              mission, settings: flightAreaSettings, validationResult: flightAreaValidation.result,
            });
            // The edit dialog returns here once it closes, saved or not (lib/missionEditNavigation.js).
            const editMission = () => navigate('/missions', { state: editFromDetailState(mission.id) });
            return (
          <Card className={needsFix ? "border-[var(--status-warning)]/30 bg-[var(--status-warning)]/5" : ""}>
            <CardHeader className="pb-3 border-b border-[var(--border-default)]">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Map className="h-5 w-5 text-[var(--brand-accent)]" />
                  {t.flightOps?.operationsPlanning || "Operations Planning"}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <OperationsPlanningEditButton
                    needsFix={needsFix}
                    onClick={editMission}
                    label={t.flightArea?.editMission || "Edit mission"}
                  />
                  {hasTakeoff && (
                    <Badge variant="outline" className="text-xs">
                      {mission.environment_type === "urban" ? (t.sora?.urban || "Urban / Populated") :
                       (t.sora?.rural || "Rural / Sparsely Populated")}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {hasTakeoff || areaKind !== FLIGHT_AREA_KIND.NONE ? (
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_24rem] gap-0">
                  <div className="min-w-0 p-4 lg:border-r border-[var(--border-default)]">
                    <MissionLocationMap
                      mode="readonly"
                      latitude={mission.latitude}
                      longitude={mission.longitude}
                      flightArea={mission.flight_area}
                      corridorWidthM={mission.corridor_width_m}
                      circleRadiusM={mission.circle_radius_m}
                      settings={flightAreaSettings}
                      validation={flightAreaValidation}
                      fitKey={mission.id}
                      height="500px"
                      showAirspaceZones={true}
                      nearbyAirports={airspaceAdvisory?.nearby_airports || []}
                      zonesDate={mission.mission_date}
                    />
                  </div>

                  <div className="min-w-0 p-4 space-y-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-1">
                          {t.flightArea?.takeoffAddress || "Take-off address"}
                        </p>
                        {/* flight_area_address is the reverse-geocoded address of the TAKE-OFF POINT
                            (mission-map plan R5 — set by the server on every mission save that changes the
                            point), not mission.location (a short freeform site name, see Missions.js's
                            "Název lokality"/"Site name" field). No fallback to mission.location — explicitly
                            requested 2026-09-16: show nothing rather than a value that only looks like an
                            address. */}
                        <p className="text-sm font-medium text-[var(--text-primary)] break-words">{mission.flight_area_address || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-1">
                          {t.flightArea?.takeoffPoint || "Take-off point"}
                        </p>
                        <p className="text-sm font-mono text-[var(--text-secondary)]">
                          {hasTakeoff ? `${mission.latitude.toFixed(6)}, ${mission.longitude.toFixed(6)}` : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-[var(--border-default)] space-y-2">
                      <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                        {t.dailyOps?.quickActions || "Quick Actions"}
                      </p>
                      <div className="grid grid-cols-1 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start gap-2 h-9"
                          disabled={!mission.flight_area_address}
                          onClick={() => {
                            navigator.clipboard.writeText(mission.flight_area_address || "");
                            toast.success(t.flightOps?.addressCopied || "Address copied");
                          }}
                          data-testid="copy-address-quick"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          {t.flightOps?.copyAddress || "Copy Address"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start gap-2 h-9"
                          disabled={!hasTakeoff}
                          onClick={() => {
                            navigator.clipboard.writeText(`${mission.latitude?.toFixed(6)}, ${mission.longitude?.toFixed(6)}`);
                            toast.success(t.flightOps?.coordinatesCopied || "Coordinates copied");
                          }}
                          data-testid="copy-coords-quick"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          {t.flightOps?.copyCoordinates || "Copy Coordinates"}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-[var(--border-default)]">
                      <div className="bg-[var(--bg-elevated)] rounded-lg p-3 border border-[var(--border-default)]">
                        <p className="text-xs font-medium text-[var(--brand-accent)] mb-2 flex items-center gap-1.5">
                          <ExternalLink className="h-3.5 w-3.5" />
                          {t.flightOps?.verifyInDroneMap || "Verify in DroneMap"}
                        </p>
                        <p className="text-xs text-[var(--text-muted)] mb-2.5">
                          {t.flightOps?.droneMapDisclaimer || "For official airspace verification"}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          onClick={() => window.open("https://dronemap.rlp.cz/", "_blank")}
                          data-testid="open-dronemap-btn"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {t.flightOps?.openDroneMap || "Open DroneMap"}
                        </Button>
                      </div>
                    </div>
                    
                    {airspaceAdvisory && (
                      <div className="pt-3 border-t border-[var(--border-default)]">
                        <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2">
                          {t.airspace?.title || "Airspace Advisory"}
                        </p>
                        <div className={`rounded-lg p-3 ${getAirspaceStatusClass(airspaceAdvisory.status)}`}>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={`text-xs ${getAirspaceBadgeClass(airspaceAdvisory.status)}`}>
                              {airspaceAdvisory.status === "high_risk" ? (t.airspace?.highRisk || "High Risk") :
                               airspaceAdvisory.status === "caution" ? (t.airspace?.caution || "Caution") :
                               airspaceAdvisory.status === "unknown" ? (t.airspace?.unknown || "Unknown") :
                               (t.airspace?.noIssues || "No Known Issues")}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {getConfidenceLabel(airspaceAdvisory.assessment_confidence)} conf.
                            </Badge>
                            {/* Data Source Indicator */}
                            {airspaceAdvisory.data_source === "openaip" ? (
                              <Badge className="text-xs bg-[var(--status-success)]/15 text-[var(--status-success)] gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--status-success)]"></span>
                                {t.sora?.dataSourceLive || "Live airspace data"}
                              </Badge>
                            ) : airspaceAdvisory.data_source === "rlp_uas_zones" ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      className="text-xs bg-[var(--status-success)]/15 text-[var(--status-success)] gap-1 cursor-help"
                                      data-testid="airspace-source-rlp"
                                    >
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--status-success)]"></span>
                                      {t.arcDisplay?.dataSourceRlp}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-[var(--bg-elevated)] border border-[var(--border-default)] text-[var(--text-primary)] max-w-xs">
                                    <p className="text-xs">{t.arcDisplay?.dataSourceRlpTooltip}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge className="text-xs bg-[var(--status-warning)]/15 text-[var(--status-warning)] gap-1 cursor-help">
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--status-warning)]"></span>
                                      {t.sora?.dataSourceStatic || "Static data (offline mode)"}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-[var(--bg-elevated)] border border-[var(--border-default)] text-[var(--text-primary)] max-w-xs">
                                    <p className="text-xs">{t.sora?.dataSourceTooltip || "OpenAIP API unavailable – using simplified airport database"}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {/* Figure 6 over the ŘLP zones: the AEC, zones and data, or the reasons of undetermined
                              airspace with their fix, then the recommendation (e.g. coordination with ATS). */}
                          {airspaceAdvisory.data_source === "rlp_uas_zones"
                            && (airspaceAdvisory.reasons?.length > 0 || airspaceAdvisory.recommendations?.length > 0) && (
                            <ul className="mb-2 space-y-1" data-testid="airspace-rlp-reasons">
                              {(airspaceAdvisory.reasons || []).map((line, idx) => (
                                <li key={`reason-${idx}`} className="text-xs text-[var(--text-secondary)]">
                                  {localizeArcLine(line, language, t, soraAssessment?.provisional_reasons)}
                                </li>
                              ))}
                              {(airspaceAdvisory.recommendations || []).map((line, idx) => (
                                <li key={`recommendation-${idx}`} className="text-xs font-medium text-[var(--text-primary)]">
                                  {localizeArcLine(line, language, t, soraAssessment?.provisional_reasons)}
                                </li>
                              ))}
                            </ul>
                          )}
                          {/* Containing Airspaces (from OpenAIP) */}
                          {airspaceAdvisory.containing_airspaces && airspaceAdvisory.containing_airspaces.length > 0 && (
                            <div className="mb-2 space-y-1">
                              {airspaceAdvisory.containing_airspaces.map((zone, idx) => (
                                <p key={`zone-${zone.type}-${zone.name?.substring(0, 10) || idx}`} className="text-xs text-[var(--text-secondary)]">
                                  <span className={`font-medium ${getZoneRiskTextClass(zone.risk_level)}`}>{zone.type}</span>: {zone.name}
                                </p>
                              ))}
                            </div>
                          )}
                          {/* Only show airport distance if no CTR/MCTR polygon exists for it */}
                          {airspaceAdvisory.nearest_airport && (() => {
                            // Check if there's a CTR/MCTR polygon that covers this airport
                            const containingAirspaces = airspaceAdvisory.containing_airspaces || [];
                            const hasCtrMctr = containingAirspaces.some(zone => 
                              ['CTR', 'MCTR', 'MATZ'].includes(zone.type)
                            );
                            // Only show airport distance if no CTR/MCTR polygon exists
                            if (hasCtrMctr) return null;
                            return (
                              <p className="text-xs text-[var(--text-secondary)]">
                                <span className="font-medium">{airspaceAdvisory.nearest_airport.name}</span>
                                <span className="text-[var(--text-muted)]"> ({airspaceAdvisory.nearest_airport.distance_km} km)</span>
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-[var(--status-warning)]/10 p-8 text-center" data-testid="mission-map-missing">
                  <AlertTriangle className="h-10 w-10 text-[var(--status-warning)] mx-auto mb-3" />
                  <p className="text-base font-medium text-[var(--status-warning)] mb-1">{t.flightArea?.legacyNoTakeoff || "This mission has no take-off point."}</p>
                  <p className="text-sm text-[var(--status-warning)]/70 mb-4">{t.flightArea?.legacyNoArea || "This mission has no flight area."}</p>
                  <Button
                    variant="outline"
                    className="gap-2 border-[var(--status-warning)] text-[var(--status-warning)] hover:bg-[var(--status-warning)]/10"
                    onClick={editMission}
                  >
                    <Pencil className="h-4 w-4" />
                    {t.flightArea?.editMission || "Edit mission"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
            );
          })()}
        </div>

        {/* Overview / Required Documents / Attachments — side by side on lg+ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 xl:gap-6 items-start">
          {/* Mission Overview Card - Compact Grid */}
          <Card>
            <CardHeader className="pb-2 border-b border-[var(--card-border)]">
              <CardTitle className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wide flex items-center gap-2">
                <FileCheck className="h-4 w-4" />
                {t.flightOps?.missionOverview || "Mission Overview"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-3">
                {/* Pilot */}
                <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="h-4 w-4 text-[var(--brand-accent)]" />
                    <span className="text-xs text-[var(--text-muted)]">{t.flightOps.pilot}</span>
                  </div>
                  <p className="font-medium text-sm text-[var(--text-primary)] truncate">
                    {pilot?.name || <span className="text-[var(--status-warning)] italic text-xs">{t.dailyOps?.unassigned || "Not assigned"}</span>}
                  </p>
                </div>
                
                {/* Aircraft */}
                <div className={`p-3 rounded-lg border relative ${
                  getSoraChange("aircraft_id") 
                    ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700" 
                    : "bg-[var(--bg-elevated)] border-[var(--border-default)]"
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Plane className="h-4 w-4 text-[var(--brand-accent)]" />
                      <span className="text-xs text-[var(--text-muted)]">{t.flightOps.aircraft}</span>
                    </div>
                    {getSoraChange("aircraft_id") && (
                      <div className="flex items-center gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge 
                                variant="outline" 
                                className="text-[10px] px-1.5 py-0 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 cursor-pointer group"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dismissSoraChange("aircraft_id");
                                }}
                              >
                                <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                                SORA
                                <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">×</span>
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="bg-[var(--bg-elevated)] border border-[var(--border-default)] text-[var(--text-primary)] max-w-xs">
                              <p className="text-xs">
                                {language === "cs" 
                                  ? `Upraveno CheckMate dne ${new Date(getSoraChange("aircraft_id").timestamp).toLocaleDateString("cs-CZ")}`
                                  : `Updated by CheckMate on ${new Date(getSoraChange("aircraft_id").timestamp).toLocaleDateString()}`
                                }
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-[10px] text-orange-600 hover:text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                          onClick={(e) => {
                            e.stopPropagation();
                            const change = getSoraChange("aircraft_id");
                            setUndoConfirm({ 
                              field: "aircraft_id", 
                              oldValue: change.old_value,
                              label: getFieldLabel("aircraft_id")
                            });
                          }}
                        >
                          {language === "cs" ? "Vrátit" : "Undo"}
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-sm text-[var(--text-primary)] truncate">
                    {aircraft?.model || <span className="text-[var(--status-warning)] italic text-xs">{t.dailyOps?.unassigned || "Not assigned"}</span>}
                  </p>
                </div>
                
                {/* Date/Time */}
                <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-[var(--brand-accent)]" />
                    <span className="text-xs text-[var(--text-muted)]">{t.flightOps.missionDate || "Date"}</span>
                  </div>
                  <p className="font-medium text-sm text-[var(--text-primary)]">
                    {formatIsoDateDmy(mission.mission_date)}
                  </p>
                </div>
                
                {/* Scenario */}
                <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-4 w-4 text-[var(--brand-accent)]" />
                    <span className="text-xs text-[var(--text-muted)]">{t.flightOps.scenario}</span>
                  </div>
                  <p className="font-medium text-sm text-[var(--text-primary)] truncate">
                    {scenario?.scenario_name || <span className="text-[var(--text-muted)] italic text-xs">{t.flightOps.noScenarioAssigned}</span>}
                  </p>
                </div>
              </div>

              {mission.notes && (
                <div className="mt-3 pt-3 border-t border-[var(--border-default)]">
                  <p className="text-xs text-[var(--text-muted)] mb-1">{t.flightOps.notes}</p>
                  <p className="text-sm text-[var(--text-secondary)] break-words">{mission.notes}</p>
                </div>
              )}
              
              {/* U-Space Authorization Status */}
              <div className="mt-3 pt-3 border-t border-[var(--border-default)]">
                <div className="flex items-center gap-2 mb-2">
                  <Radar className="h-4 w-4 text-blue-500" />
                  <span className="text-xs text-[var(--text-muted)]">{t.uSpace?.uSpaceAuthorization || "U-Space Authorization"}</span>
                </div>
                {isInUSpaceZone ? (
                  <Badge className="bg-amber-100 text-amber-800 gap-1 px-2 py-1">
                    <AlertTriangle className="h-3 w-3" />
                    {t.uSpace?.uSpaceApiNotConnected || "API Not Connected"}
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 gap-1 px-2 py-1">
                    {t.uSpace?.uSpaceNotRequired || "Not Required"}
                  </Badge>
                )}
                {isInUSpaceZone && (
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {language === "cs" 
                      ? "USSP integrace zatím není k dispozici" 
                      : "USSP integration not yet available"}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Required Documents - Clearer Status */}
          <Card>
            <CardHeader className="pb-2 border-b border-[var(--card-border)]">
              <CardTitle className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wide flex items-center gap-2">
                <FileCheck className="h-4 w-4" />
                {t.requiredDocs.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              {getRelevantDocuments().length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-4">{t.complianceModule.noDocumentsFound}</p>
              ) : (
                <div className="space-y-2">
                  {getRelevantDocuments().map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-2.5 bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-default)]">
                      <span className="truncate text-sm text-[var(--text-primary)]">{doc.document_name}</span>
                      <Badge 
                        variant="outline" 
                        className={`text-xs flex-shrink-0 ${
                          doc.document_status === "valid" ? "bg-[var(--status-success)]/10 text-[var(--status-success)] border-[var(--status-success)]/30" :
                          doc.document_status === "expiring" ? "bg-[var(--status-warning)]/10 text-[var(--status-warning)] border-[var(--status-warning)]/30" :
                          "bg-[var(--status-critical)]/10 text-[var(--status-critical)] border-[var(--status-critical)]/30"
                        }`}
                      >
                        {doc.document_status === "valid" ? t.requiredDocs.valid :
                         doc.document_status === "expiring" ? t.requiredDocs.expiring :
                         t.requiredDocs.expired}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mission Attachments */}
          <Card>
            <CardHeader className="pb-2 border-b border-[var(--card-border)]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wide flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  {t.flightOps?.attachments || "Attachments"}
                  {mission.attachments?.length > 0 && (
                    <span className="bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] px-2 py-0.5 rounded-full text-xs font-normal">
                      {mission.attachments.length}
                    </span>
                  )}
                </CardTitle>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-1.5"
                    data-testid="upload-attachment-btn"
                  >
                    {uploading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {t.actions?.upload || "Upload"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-3">
              {(!mission.attachments || mission.attachments.length === 0) ? (
                <div className="text-center py-6 border-2 border-dashed border-[var(--border-default)] rounded-lg">
                  <Paperclip className="h-8 w-8 mx-auto mb-2 text-[var(--text-muted)]/30" />
                  <p className="text-sm text-[var(--text-muted)]">{t.flightOps?.noAttachments || "No attachments"}</p>
                  <p className="text-xs text-[var(--text-muted)]/70 mt-1">{t.common?.dropFilesHint || "Drop files here or click Upload"}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {mission.attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between p-2 bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-default)] hover:border-[var(--brand-accent)]/30 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`p-1.5 rounded ${isImageFile(att.mime_type) ? 'bg-[var(--brand-accent)]/10' : 'bg-[var(--bg-main)]'}`}>
                          {isImageFile(att.mime_type) ? (
                            <Image className="h-4 w-4 text-[var(--brand-accent)]" />
                          ) : (
                            <FileText className="h-4 w-4 text-[var(--text-muted)]" />
                          )}
                        </div>
                        <span className="text-xs truncate text-[var(--text-primary)]">{att.file_name}</span>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {isImageFile(att.mime_type) && (
                          <Button variant="ghost" size="sm" onClick={() => previewAttachment(att)} className="h-7 w-7 p-0 hover:bg-[var(--brand-accent)]/10">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => downloadAttachment(att)} className="h-7 w-7 p-0 hover:bg-[var(--brand-accent)]/10">
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteAttachment(att)} className="h-7 w-7 p-0 text-[var(--status-critical)] hover:text-[var(--status-critical)] hover:bg-[var(--status-critical)]/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ============================================
          PRE-FLIGHT CHECKLIST
          ============================================ */}
      <Card>
            <CardHeader className="pb-3 border-b border-[var(--card-border)]">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 text-[var(--brand-accent)]" />
                  <CardTitle className="text-lg text-[var(--text-primary)]">
                    {t.subNav.missionChecklist}
                  </CardTitle>
                </div>
                <Button 
                  onClick={saveChecklist} 
                  disabled={saving}
                  size="sm"
                  className="gap-2"
                  data-testid="save-checklist-btn"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {t.actions.save}
                </Button>
              </div>
              
              {/* Progress Bar - More Prominent */}
              <div className="mt-4 bg-[var(--bg-elevated)] rounded-lg p-3 border border-[var(--border-default)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--text-primary)]">
                    {checklistProgress.percent === 100 ? "✓ Checklist Complete" : "Progress"}
                  </span>
                  <span className={`text-lg font-bold ${checklistProgress.percent === 100 ? 'text-[var(--status-success)]' : 'text-[var(--brand-accent)]'}`}>
                    {checklistProgress.percent}%
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2.5 bg-[var(--bg-main)] rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${checklistProgress.percent === 100 ? 'bg-[var(--status-success)]' : 'bg-[var(--brand-accent)]'}`}
                      style={{ width: `${checklistProgress.percent}%` }}
                    />
                  </div>
                  <span className="text-sm text-[var(--text-muted)] whitespace-nowrap">
                    {checklistProgress.completed}/{checklistProgress.total}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {checklist.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-8">{t.flightOps.noChecklistAvailable}</p>
              ) : (
                <div className="space-y-3">
                  {checklist.map((section, sectionIdx) => {
                    const sectionComplete = section.items.filter(i => i.status === "completed").length;
                    const sectionTotal = section.items.length;
                    const allComplete = sectionComplete === sectionTotal;
                    
                    return (
                      <div key={`section-${section.section}-${sectionIdx}`} className={`border rounded-lg overflow-hidden ${allComplete ? 'border-[var(--status-success)]/30' : 'border-[var(--border-default)]'}`}>
                        <button
                          onClick={() => toggleSection(sectionIdx)}
                          className={`w-full flex items-center justify-between p-3 ${allComplete ? 'bg-[var(--status-success)]/5' : 'bg-[var(--bg-elevated)]'} hover:bg-[var(--bg-elevated)] transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            {allComplete ? (
                              <CheckCircle2 className="h-5 w-5 text-[var(--status-success)]" />
                            ) : (
                              <Clock className="h-5 w-5 text-[var(--text-muted)]" />
                            )}
                            <span className="font-semibold text-sm text-[var(--text-primary)]">{translateChecklistSection(section.section)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${allComplete ? 'bg-[var(--status-success)]/15 text-[var(--status-success)]' : 'bg-[var(--bg-main)] text-[var(--text-muted)]'}`}>
                              {sectionComplete}/{sectionTotal}
                            </span>
                            {expandedSections[sectionIdx] ? (
                              <ChevronUp className="h-4 w-4 text-[var(--text-muted)]" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-[var(--text-muted)]" />
                            )}
                          </div>
                        </button>
                        
                        {expandedSections[sectionIdx] && (
                          <div className="p-3 space-y-2 bg-[var(--bg-main)]">
                            {section.items.map((item, itemIdx) => (
                              <div 
                                key={`item-${item.key || item.label?.substring(0, 15) || itemIdx}`} 
                                className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                  item.status === "completed" ? "bg-[var(--status-success)]/5" : "hover:bg-[var(--bg-elevated)]"
                                }`}
                              >
                                <Checkbox
                                  id={`${sectionIdx}-${itemIdx}`}
                                  checked={item.status === "completed"}
                                  onCheckedChange={(checked) => {
                                    updateChecklistItem(sectionIdx, itemIdx, "status", checked ? "completed" : "not_started");
                                  }}
                                  data-testid={`checklist-${item.key}`}
                                  className="h-5 w-5"
                                />
                                <Label 
                                  htmlFor={`${sectionIdx}-${itemIdx}`}
                                  className={`cursor-pointer flex-1 text-sm ${item.status === "completed" ? "text-[var(--text-muted)] line-through" : "text-[var(--text-primary)]"}`}
                                >
                                  {translateChecklistItem(item.label)}
                                </Label>
                                {item.status === "completed" && (
                                  <CheckCircle2 className="h-4 w-4 text-[var(--status-success)]" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Wave 3 Block 3A: Contacts Tab */}
        <TabsContent value="contacts">
          <div className="max-w-2xl mx-auto">
            <MissionContactsCard
              missionId={mission.id}
              language={language}
            />
          </div>
        </TabsContent>
        
        {/* Sprint Y Block E: Correspondence Tab Content */}
        <TabsContent value="correspondence">
          <div className="max-w-4xl mx-auto">
            <CorrespondenceTab
              entityType="mission"
              entityId={mission.id}
              includeProposed={true}
              compact={false}
              maxItems={20}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="sora-assistant">
          <FeatureGate 
            feature="checkmate"
            featureLabel={t.subscription?.featureLabels?.checkmate || "CheckMate SORA 2.5 Engine"}
            hasAccess={hasFeature('checkmate')}
            requiredTier="professional"
            language={language}
          >
            <SoraAssistant
              missionId={mission.id}
              mission={mission}
              aircraft={aircraft}
              onMissionUpdate={refreshMissionSoraData}
              onNavigateToProvisionalTarget={navigateToProvisionalTarget}
              isProvisionalTargetActionable={isProvisionalTargetActionable}
            />
          </FeatureGate>
        </TabsContent>
      </Tabs>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl bg-[var(--card-bg)] border-[var(--card-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)]">{t.flightOps?.imagePreview || "Image Preview"}</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <img 
              src={previewImage}
              alt="Preview"
              className="w-full h-auto rounded-lg"
              onError={(e) => {
                e.target.src = "";
                toast.error(t.flightOps?.imageLoadError || "Failed to load image");
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* SORA Undo Confirmation Dialog */}
      <Dialog open={!!undoConfirm} onOpenChange={() => setUndoConfirm(null)}>
        <DialogContent className="max-w-sm bg-[var(--card-bg)] border-[var(--card-border)]" data-testid="undo-sora-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)]">
              {t.soraAssistant?.undoTitle || "Revert Change"}
            </DialogTitle>
          </DialogHeader>
          {undoConfirm && (
            <div className="py-4">
              <p className="text-sm text-[var(--text-secondary)]">
                {language === "cs" 
                  ? `Vrátit ${undoConfirm.label} na původní hodnotu ${undoConfirm.field === "max_altitude_m" ? `${undoConfirm.oldValue}m` : undoConfirm.oldValue}?`
                  : `Revert ${undoConfirm.label} to original value ${undoConfirm.field === "max_altitude_m" ? `${undoConfirm.oldValue}m` : undoConfirm.oldValue}?`
                }
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setUndoConfirm(null)}>
              {t.actions?.cancel || "Cancel"}
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => undoConfirm && undoSoraChange(undoConfirm.field)}
              data-testid="confirm-undo-btn"
            >
              {t.soraAssistant?.revert || "Revert"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* UCL Submission Confirmation Modal */}
      <Dialog open={showSubmissionModal} onOpenChange={setShowSubmissionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-indigo-600" />
              {language === 'cs' ? 'Vytvořit podání ÚCL' : 'Create UCL Submission'}
            </DialogTitle>
            <DialogDescription>
              {language === 'cs' 
                ? 'Vytvoří se nové podání k Úřadu civilního letectví s následujícími údaji:' 
                : 'A new submission to the Civil Aviation Authority will be created with:'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium">{language === 'cs' ? 'Mise' : 'Mission'}</span>
              <span className="text-sm text-slate-600">{mission?.mission_id}</span>
            </div>
            
            {scenario && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium">{language === 'cs' ? 'Scénář' : 'Scenario'}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                    {scenario.scenario_type}
                  </Badge>
                  <span className="text-sm text-slate-600">{scenario.scenario_name}</span>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium">{language === 'cs' ? 'Dokumenty' : 'Documents'}</span>
              <Badge variant="outline">{missionDocuments.length}</Badge>
            </div>
            
            {soraAssessment && (() => {
              const sailLevelStr = soraAssessment.estimated_sail_level || soraAssessment.sail_level || '';
              const isOutOfScope = sailLevelStr.toUpperCase() === 'OUT_OF_SCOPE';
              return (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">SAIL</span>
                  <Badge className={`${isOutOfScope ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'} gap-1`}>
                    {isOutOfScope ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA') : (sailLevelStr || '?')}
                    {soraAssessment.provisional_result && (
                      <span title={language === 'cs' ? 'Předběžný odhad' : 'Preliminary estimate'}>
                        <AlertTriangle className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                </div>
              );
            })()}
            {soraAssessment?.provisional_result && (
              <p className="text-xs text-[var(--status-warning)] flex items-center gap-1.5 px-1">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                {language === 'cs'
                  ? 'Předběžný odhad ze zjednodušeného výpočtu.'
                  : 'Preliminary estimate from a simplified calculation.'}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmissionModal(false)}>
              {t.actions?.cancel || "Cancel"}
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
              onClick={createUclSubmission}
              disabled={creatingSubmission}
              data-testid="confirm-create-submission-btn"
            >
              {creatingSubmission ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {language === 'cs' ? 'Vytvořit' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissionDetail;
